<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  bulkDeleteLilithDocuments,
  deleteLilithDocument,
  bulkSyncLilithDocuments,
  createLilithFileDocument,
  createLilithWebsiteDocument,
  getLilithDocument,
  getLilithDocumentFaqs,
  getLilithDocumentUsage,
  getLilithDocuments,
  syncLilithDocument,
  retryLilithDocument,
} from 'dashboard/api/lilithKnowledge';

const props = defineProps({
  assistantId: {
    type: String,
    required: true,
  },
});

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const PROCESSING_POLL_MS = 5000;
let disposed = false;

const { accountId } = useAccount();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const records = ref([]);
const meta = ref({ page: 1, total_count: 0, total_pages: 1 });
const capabilities = ref({
  can_manage_documents: false,
  can_view_document_usage: false,
});
const loading = ref(false);
const error = ref('');
const search = ref('');
const sourceType = ref('all');
const syncStatus = ref('all');
const sort = ref('recently_updated');
const selectedIds = ref(new Set());
const selectedDocument = ref(null);
const selectedFaqs = ref([]);
const selectedFaqMeta = ref({ page: 1, total_count: 0, total_pages: 1 });
const selectedUsage = ref([]);
const selectedUsageMeta = ref({ page: 1, total_count: 0, total_pages: 1 });
const detailsLoading = ref(false);
const detailsError = ref('');
const contentView = ref('formatted');
const submitting = ref(false);
const syncing = ref(false);
let requestSequence = 0;
let detailsSequence = 0;
let pollTimer = null;

const addForm = reactive({
  open: false,
  kind: 'website',
  name: '',
  url: '',
  file: null,
});

const canManageDocuments = computed(
  () => capabilities.value.can_manage_documents
);
const canViewDocumentUsage = computed(
  () => capabilities.value.can_view_document_usage
);
const hasProcessingSources = computed(
  () =>
    records.value.some(
      item =>
        item.status === 'PROCESSING' ||
        item.sync_status === 'SYNCING' ||
        ['PENDING', 'RUNNING', 'RETRYING'].includes(
          item.metadata?.derivation_status
        )
    ) ||
    selectedDocument.value?.status === 'PROCESSING' ||
    selectedDocument.value?.sync_status === 'SYNCING' ||
    ['PENDING', 'RUNNING', 'RETRYING'].includes(
      selectedDocument.value?.metadata?.derivation_status
    )
);
const allCurrentPageSelected = computed(
  () =>
    records.value.length > 0 &&
    records.value.every(item => selectedIds.value.has(item.id))
);

const requestErrorText = requestError =>
  requestError?.response?.data?.error ||
  requestError?.message ||
  t('TEAM_LILITH.DOCUMENTS.ERROR');

const statusLabel = status => {
  switch (status) {
    case 'PROCESSING':
      return t('TEAM_LILITH.DOCUMENTS.STATUS.PROCESSING');
    case 'AVAILABLE':
      return t('TEAM_LILITH.DOCUMENTS.STATUS.AVAILABLE');
    case 'FAILED':
      return t('TEAM_LILITH.DOCUMENTS.STATUS.FAILED');
    case 'ARCHIVED':
      return t('TEAM_LILITH.DOCUMENTS.STATUS.ARCHIVED');
    default:
      return t('TEAM_LILITH.DOCUMENTS.STATUS.UNKNOWN');
  }
};

const derivationLabel = item => {
  const status = item.metadata?.derivation_status;
  if (!status) return '';
  const metadata = item.metadata;
  let label = t(`TEAM_LILITH.DOCUMENTS.DERIVATION.${status}`);
  if (
    ['PENDING', 'RUNNING', 'RETRYING', 'FAILED'].includes(status) &&
    metadata.derivation_total_chars > 0
  ) {
    const completed = metadata.derivation_completed_chars || 0;
    const total = metadata.derivation_total_chars;
    label +=
      ' · ' +
      t('TEAM_LILITH.DOCUMENTS.DERIVATION_PROGRESS', {
        percent: Math.min(99, Math.floor((completed * 100) / total)),
        completed,
        total,
      });
  }
  if (
    ['RUNNING', 'RETRYING', 'FAILED'].includes(status) &&
    metadata.derivation_attempt
  ) {
    label +=
      ' · ' +
      t('TEAM_LILITH.DOCUMENTS.DERIVATION_ATTEMPT', {
        attempt: metadata.derivation_attempt,
        max: metadata.derivation_max_attempts || 4,
      });
  }
  if (status === 'RETRYING' && metadata.derivation_retry_at) {
    label +=
      ' · ' +
      t('TEAM_LILITH.DOCUMENTS.DERIVATION_RETRY_AT', {
        time: new Date(metadata.derivation_retry_at).toLocaleTimeString(),
      });
  }
  if (status === 'SUCCEEDED') {
    label +=
      ' · ' +
      t('TEAM_LILITH.DOCUMENTS.DERIVATION_COUNT', {
        count: metadata.derivation_candidate_count || 0,
      });
  }
  return label;
};

const sourceTypeLabel = item =>
  item.source_type === 'FILE'
    ? t('TEAM_LILITH.DOCUMENTS.TYPE_FILE')
    : t('TEAM_LILITH.DOCUMENTS.TYPE_WEBSITE');

const syncStatusLabel = status => {
  switch (status) {
    case 'SYNCED':
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.SYNCED');
    case 'STALE':
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.STALE');
    case 'SYNCING':
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.SYNCING');
    case 'FAILED':
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.FAILED');
    case 'NEVER':
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.NEVER');
    default:
      return t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.UNKNOWN');
  }
};

const resetProcessingPoll = () => {
  if (pollTimer) window.clearTimeout(pollTimer);
  pollTimer = null;
};

let scheduleProcessingPoll = () => undefined;

const fetchRecords = async (page = 1, { quiet = false } = {}) => {
  requestSequence += 1;
  const sequence = requestSequence;
  if (!quiet) loading.value = true;
  error.value = '';
  selectedIds.value = new Set();
  try {
    const params = { page, sort: sort.value };
    const query = search.value.trim();
    if (query) params.search = query;
    if (sourceType.value !== 'all') params.source_type = sourceType.value;
    if (sourceType.value === 'WEBSITE' && syncStatus.value !== 'all') {
      params.sync_status = syncStatus.value;
    }
    const response = await getLilithDocuments(
      accountId.value,
      props.assistantId,
      params
    );
    if (sequence !== requestSequence) return;
    records.value = response.data?.payload || [];
    meta.value = response.data?.meta || {
      page,
      total_count: 0,
      total_pages: 1,
    };
    capabilities.value = response.data?.capabilities || capabilities.value;
  } catch (requestError) {
    if (sequence !== requestSequence) return;
    error.value = requestErrorText(requestError);
  } finally {
    if (sequence === requestSequence && !quiet) loading.value = false;
    if (sequence === requestSequence) scheduleProcessingPoll();
  }
};

const applyRelatedFaqs = (response, page) => {
  selectedFaqs.value = response.data?.payload || [];
  selectedFaqMeta.value = response.data?.meta || {
    page,
    total_count: 0,
    total_pages: 1,
  };
};

const fetchRelatedFaqs = async (id, page = 1) => {
  const response = await getLilithDocumentFaqs(
    accountId.value,
    props.assistantId,
    id,
    { page }
  );
  applyRelatedFaqs(response, page);
};

const applyUsage = (response, page) => {
  selectedUsage.value = response.data?.payload || [];
  selectedUsageMeta.value = response.data?.meta || {
    page,
    total_count: 0,
    total_pages: 1,
  };
};

const fetchUsage = async (id, page = 1) => {
  if (!canViewDocumentUsage.value) return;
  const response = await getLilithDocumentUsage(
    accountId.value,
    props.assistantId,
    id,
    { page }
  );
  applyUsage(response, page);
};

const conversationHref = conversationRef =>
  `/app/accounts/${accountId.value}/conversations/${conversationRef}`;

const formattedUsageDate = value =>
  value ? new Date(value).toLocaleString() : '—';

const fetchDetails = async (id, { quiet = false } = {}) => {
  detailsSequence += 1;
  const sequence = detailsSequence;
  if (!quiet) detailsLoading.value = true;
  detailsError.value = '';
  try {
    const [documentResponse, faqResponse] = await Promise.all([
      getLilithDocument(accountId.value, props.assistantId, id),
      getLilithDocumentFaqs(accountId.value, props.assistantId, id, {
        page: 1,
      }),
    ]);
    if (sequence !== detailsSequence) return;
    selectedDocument.value = documentResponse.data || null;
    applyRelatedFaqs(faqResponse, 1);
    if (documentResponse.data?.capabilities) {
      capabilities.value = documentResponse.data.capabilities;
    }
    if (capabilities.value.can_view_document_usage) {
      await fetchUsage(id, 1);
    } else {
      selectedUsage.value = [];
      selectedUsageMeta.value = { page: 1, total_count: 0, total_pages: 1 };
    }
  } catch (requestError) {
    if (sequence !== detailsSequence) return;
    detailsError.value = requestErrorText(requestError);
  } finally {
    if (sequence === detailsSequence && !quiet) detailsLoading.value = false;
    if (sequence === detailsSequence) scheduleProcessingPoll();
  }
};

scheduleProcessingPoll = () => {
  if (pollTimer) window.clearTimeout(pollTimer);
  pollTimer = null;
  if (disposed) return;
  if (!hasProcessingSources.value) {
    return;
  }
  pollTimer = window.setTimeout(async () => {
    await fetchRecords(meta.value.page || 1, { quiet: true });
    if (selectedDocument.value?.id) {
      await fetchDetails(selectedDocument.value.id, { quiet: true });
    }
    scheduleProcessingPoll();
  }, PROCESSING_POLL_MS);
};

const openDetails = async id => {
  await router.replace({
    query: { ...route.query, source_id: id },
  });
};

const retryOne = async item => {
  if (!canManageDocuments.value || syncing.value) return;
  syncing.value = true;
  try {
    await retryLilithDocument(accountId.value, props.assistantId, item.id);
    resetProcessingPoll();
    await fetchRecords(meta.value.page || 1);
    if (selectedDocument.value?.id === item.id) await openDetails(item.id);
    scheduleProcessingPoll();
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  } finally {
    syncing.value = false;
  }
};

const closeDetails = async () => {
  const nextQuery = { ...route.query };
  delete nextQuery.source_id;
  selectedDocument.value = null;
  selectedFaqs.value = [];
  selectedUsage.value = [];
  selectedUsageMeta.value = { page: 1, total_count: 0, total_pages: 1 };
  detailsError.value = '';
  await router.replace({ query: nextQuery });
  scheduleProcessingPoll();
};

const debouncedSearch = debounce(() => fetchRecords(1), 300);

const resetAddForm = () => {
  Object.assign(addForm, {
    open: false,
    kind: 'website',
    name: '',
    url: '',
    file: null,
  });
};

const handleFile = event => {
  const [file] = Array.from(event.target.files || []);
  addForm.file = file || null;
};

const submitSource = async () => {
  if (!canManageDocuments.value || submitting.value) return;
  const name = addForm.name.trim();
  try {
    submitting.value = true;
    if (addForm.kind === 'website') {
      const url = addForm.url.trim();
      if (!url) return;
      await createLilithWebsiteDocument(accountId.value, props.assistantId, {
        url,
        ...(name ? { name } : {}),
      });
    } else {
      const file = addForm.file;
      if (!file) return;
      if (!/\.(txt|md|markdown|html|htm)$/i.test(file.name)) {
        useAlert(t('TEAM_LILITH.DOCUMENTS.FILE_ONLY'));
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        useAlert(t('TEAM_LILITH.DOCUMENTS.FILE_TOO_LARGE'));
        return;
      }
      await createLilithFileDocument(
        accountId.value,
        props.assistantId,
        file,
        name
      );
    }
    resetAddForm();
    resetProcessingPoll();
    await fetchRecords(1);
    scheduleProcessingPoll();
    useAlert(t('TEAM_LILITH.DOCUMENTS.CREATED'));
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  } finally {
    submitting.value = false;
  }
};

const toggleSelected = id => {
  const next = new Set(selectedIds.value);
  next[next.has(id) ? 'delete' : 'add'](id);
  selectedIds.value = next;
};

const toggleCurrentPage = () => {
  const next = new Set(selectedIds.value);
  if (allCurrentPageSelected.value) {
    records.value.forEach(item => next.delete(item.id));
  } else {
    records.value.forEach(item => next.add(item.id));
  }
  selectedIds.value = next;
};

const deleteOne = async item => {
  if (!canManageDocuments.value) return;
  try {
    await deleteLilithDocument(accountId.value, props.assistantId, item.id);
    if (selectedDocument.value?.id === item.id) await closeDetails();
    selectedIds.value.delete(item.id);
    await fetchRecords(meta.value.page || 1);
    useAlert(t('TEAM_LILITH.DOCUMENTS.DELETED'));
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  }
};

const deleteSelected = async () => {
  if (!canManageDocuments.value || !selectedIds.value.size) return;
  try {
    await bulkDeleteLilithDocuments(
      accountId.value,
      props.assistantId,
      Array.from(selectedIds.value)
    );
    if (
      selectedDocument.value &&
      selectedIds.value.has(selectedDocument.value.id)
    ) {
      await closeDetails();
    }
    selectedIds.value = new Set();
    await fetchRecords(meta.value.page || 1);
    useAlert(t('TEAM_LILITH.DOCUMENTS.DELETED'));
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  }
};

const syncOne = async item => {
  if (
    !canManageDocuments.value ||
    syncing.value ||
    item.source_type !== 'WEBSITE' ||
    item.status !== 'AVAILABLE'
  ) {
    return;
  }
  try {
    syncing.value = true;
    await syncLilithDocument(accountId.value, props.assistantId, item.id);
    resetProcessingPoll();
    await fetchRecords(meta.value.page || 1);
    if (selectedDocument.value?.id === item.id) {
      await fetchDetails(item.id, { quiet: true });
    }
    scheduleProcessingPoll();
    useAlert(t('TEAM_LILITH.DOCUMENTS.SYNC_QUEUED'));
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  } finally {
    syncing.value = false;
  }
};

const syncSelected = async () => {
  if (!canManageDocuments.value || !selectedIds.value.size || syncing.value) {
    return;
  }
  try {
    syncing.value = true;
    const response = await bulkSyncLilithDocuments(
      accountId.value,
      props.assistantId,
      Array.from(selectedIds.value)
    );
    resetProcessingPoll();
    await fetchRecords(meta.value.page || 1);
    scheduleProcessingPoll();
    useAlert(
      t('TEAM_LILITH.DOCUMENTS.SYNC_SELECTED_QUEUED', {
        count: response.data?.queued_count || 0,
      })
    );
  } catch (requestError) {
    useAlert(requestErrorText(requestError));
  } finally {
    syncing.value = false;
  }
};

const copyContent = async () => {
  const content = selectedDocument.value?.content || '';
  if (!content) return;
  try {
    await navigator.clipboard.writeText(content);
    useAlert(t('TEAM_LILITH.DOCUMENTS.COPIED'));
  } catch {
    useAlert(t('TEAM_LILITH.DOCUMENTS.COPY_FAILED'));
  }
};

watch(sourceType, value => {
  if (value !== 'WEBSITE' && syncStatus.value !== 'all') {
    syncStatus.value = 'all';
    return;
  }
  fetchRecords(1);
});
watch([sort, syncStatus], () => fetchRecords(1));
watch(
  () => props.assistantId,
  () => {
    selectedIds.value = new Set();
    selectedDocument.value = null;
    selectedFaqs.value = [];
    selectedUsage.value = [];
    selectedUsageMeta.value = { page: 1, total_count: 0, total_pages: 1 };
    detailsError.value = '';
    resetAddForm();
    resetProcessingPoll();
    fetchRecords(1);
  }
);
watch(
  () => route.query.source_id,
  sourceId => {
    if (typeof sourceId === 'string' && sourceId) {
      fetchDetails(sourceId);
    } else {
      selectedDocument.value = null;
      selectedFaqs.value = [];
      selectedUsage.value = [];
      selectedUsageMeta.value = { page: 1, total_count: 0, total_pages: 1 };
      detailsError.value = '';
      scheduleProcessingPoll();
    }
  }
);

onMounted(async () => {
  await fetchRecords(1);
  const sourceId = route.query.source_id;
  if (typeof sourceId === 'string' && sourceId) await fetchDetails(sourceId);
});

onBeforeUnmount(() => {
  disposed = true;
  if (pollTimer) window.clearTimeout(pollTimer);
});
</script>

<template>
  <div class="flex-1 overflow-y-auto bg-n-surface-1 px-6 py-8 lg:px-10">
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-5">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.DOCUMENTS.EYEBROW') }}
          </div>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.DOCUMENTS.TITLE') }}
          </h1>
          <p class="mt-1 max-w-3xl text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.DOCUMENTS.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="canManageDocuments"
          :label="$t('TEAM_LILITH.DOCUMENTS.ADD')"
          icon="i-lucide-plus"
          @click="addForm.open = !addForm.open"
        />
      </header>

      <section
        v-if="addForm.open && canManageDocuments"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="mb-4 flex gap-2">
          <button
            type="button"
            class="rounded-lg px-3 py-2 text-sm"
            :class="
              addForm.kind === 'website'
                ? 'bg-n-solid-2 text-n-slate-12'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
            @click="addForm.kind = 'website'"
          >
            {{ $t('TEAM_LILITH.DOCUMENTS.TYPE_WEBSITE') }}
          </button>
          <button
            type="button"
            class="rounded-lg px-3 py-2 text-sm"
            :class="
              addForm.kind === 'file'
                ? 'bg-n-solid-2 text-n-slate-12'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
            @click="addForm.kind = 'file'"
          >
            {{ $t('TEAM_LILITH.DOCUMENTS.TYPE_FILE') }}
          </button>
        </div>
        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="addForm.name"
            :label="$t('TEAM_LILITH.DOCUMENTS.NAME')"
            :placeholder="$t('TEAM_LILITH.DOCUMENTS.NAME_OPTIONAL')"
          />
          <Input
            v-if="addForm.kind === 'website'"
            v-model="addForm.url"
            type="url"
            :label="$t('TEAM_LILITH.DOCUMENTS.URL')"
            placeholder="https://example.com/help"
          />
          <label v-else class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ $t('TEAM_LILITH.DOCUMENTS.FILE') }}
            <input
              type="file"
              accept=".txt,.md,.markdown,.html,.htm,text/plain,text/markdown,text/html"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-n-slate-12"
              @change="handleFile"
            />
            <span class="text-xs text-n-slate-9">
              {{ $t('TEAM_LILITH.DOCUMENTS.FILE_LIMIT') }}
            </span>
          </label>
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <Button
            :label="$t('TEAM_LILITH.DOCUMENTS.CANCEL')"
            variant="ghost"
            color="slate"
            @click="resetAddForm"
          />
          <Button
            :label="$t('TEAM_LILITH.DOCUMENTS.ADD_SOURCE')"
            :disabled="submitting"
            @click="submitSource"
          />
        </div>
      </section>

      <div class="flex flex-wrap items-center gap-3">
        <Input
          v-model="search"
          class="min-w-64 flex-1"
          size="sm"
          type="search"
          :placeholder="$t('TEAM_LILITH.DOCUMENTS.SEARCH')"
          @input="debouncedSearch"
        />
        <select
          v-model="sourceType"
          class="h-9 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12"
        >
          <option value="all">
            {{ $t('TEAM_LILITH.DOCUMENTS.ALL_TYPES') }}
          </option>
          <option value="WEBSITE">
            {{ $t('TEAM_LILITH.DOCUMENTS.TYPE_WEBSITE') }}
          </option>
          <option value="FILE">
            {{ $t('TEAM_LILITH.DOCUMENTS.TYPE_FILE') }}
          </option>
        </select>
        <select
          v-if="sourceType === 'WEBSITE'"
          v-model="syncStatus"
          class="h-9 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12"
        >
          <option value="all">
            {{ $t('TEAM_LILITH.DOCUMENTS.ALL_SYNC_STATES') }}
          </option>
          <option value="SYNCED">
            {{ $t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.SYNCED') }}
          </option>
          <option value="STALE">
            {{ $t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.STALE') }}
          </option>
          <option value="SYNCING">
            {{ $t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.SYNCING') }}
          </option>
          <option value="FAILED">
            {{ $t('TEAM_LILITH.DOCUMENTS.SYNC_STATUS.FAILED') }}
          </option>
        </select>
        <select
          v-model="sort"
          class="h-9 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12"
        >
          <option value="recently_updated">
            {{ $t('TEAM_LILITH.DOCUMENTS.SORT_UPDATED') }}
          </option>
          <option value="recently_created">
            {{ $t('TEAM_LILITH.DOCUMENTS.SORT_CREATED') }}
          </option>
          <option v-if="canViewDocumentUsage" value="most_used">
            {{ $t('TEAM_LILITH.DOCUMENTS.SORT_MOST_USED') }}
          </option>
        </select>
        <label
          v-if="canManageDocuments && records.length"
          class="flex h-9 items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="allCurrentPageSelected"
            @change="toggleCurrentPage"
          />
          {{ $t('TEAM_LILITH.DOCUMENTS.SELECT_PAGE') }}
        </label>
      </div>

      <div
        v-if="canManageDocuments && selectedIds.size"
        class="flex items-center justify-between rounded-lg border border-n-weak bg-n-alpha-1 px-4 py-3"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('TEAM_LILITH.DOCUMENTS.SELECTED', { count: selectedIds.size })
          }}
        </span>
        <div class="flex items-center gap-2">
          <Button
            :label="$t('TEAM_LILITH.DOCUMENTS.SYNC_SELECTED')"
            variant="ghost"
            color="slate"
            :disabled="syncing"
            @click="syncSelected"
          />
          <Button
            :label="$t('TEAM_LILITH.DOCUMENTS.DELETE_SELECTED')"
            variant="faded"
            color="ruby"
            @click="deleteSelected"
          />
        </div>
      </div>

      <div
        v-if="loading"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-8 text-center text-sm text-n-slate-10"
      >
        {{ $t('TEAM_LILITH.DOCUMENTS.LOADING') }}
      </div>
      <div
        v-else-if="error"
        class="rounded-xl border border-n-ruby-6 bg-n-ruby-2 p-4 text-sm text-n-ruby-11"
      >
        {{ error }}
      </div>
      <div
        v-else-if="!records.length"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-8 text-center text-sm text-n-slate-10"
      >
        {{
          search.trim() || sourceType !== 'all' || syncStatus !== 'all'
            ? $t('TEAM_LILITH.DOCUMENTS.EMPTY_FILTERED')
            : $t('TEAM_LILITH.DOCUMENTS.EMPTY')
        }}
      </div>
      <div v-else class="flex flex-col gap-3">
        <article
          v-for="item in records"
          :key="item.id"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
        >
          <div class="flex items-start gap-3">
            <input
              v-if="canManageDocuments"
              type="checkbox"
              class="mt-1"
              :checked="selectedIds.has(item.id)"
              @change="toggleSelected(item.id)"
            />
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="truncate text-sm font-semibold text-n-slate-12">
                  {{ item.name }}
                </h2>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
                >
                  {{ sourceTypeLabel(item) }}
                </span>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
                >
                  {{ statusLabel(item.status) }}
                </span>
                <span
                  v-if="item.source_type === 'WEBSITE'"
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
                >
                  {{ syncStatusLabel(item.sync_status) }}
                </span>
              </div>
              <p class="mt-1 truncate text-xs text-n-slate-9">
                {{ item.external_url || item.file_name }}
              </p>
              <p
                v-if="canViewDocumentUsage"
                class="mt-1 text-xs text-n-slate-9"
              >
                {{
                  $t('TEAM_LILITH.DOCUMENTS.USAGE_COUNT', {
                    count: item.usage_count || 0,
                  })
                }}
              </p>
              <p
                v-if="item.status === 'FAILED' && item.error_summary"
                class="mt-2 text-sm text-n-ruby-11"
              >
                {{ item.error_summary }}
              </p>
              <p
                v-if="
                  item.source_type === 'WEBSITE' &&
                  item.sync_status === 'FAILED'
                "
                class="mt-2 text-sm text-n-ruby-11"
              >
                {{
                  item.sync_error_summary ||
                  $t('TEAM_LILITH.DOCUMENTS.SYNC_ERROR_FALLBACK')
                }}
              </p>
              <p
                v-if="item.metadata?.discovery_limited"
                class="mt-2 text-sm text-n-slate-11"
              >
                {{ $t('TEAM_LILITH.DOCUMENTS.DISCOVERY_LIMITED') }}
              </p>
              <p
                v-if="derivationLabel(item)"
                class="mt-2 text-sm text-n-slate-11"
              >
                {{ derivationLabel(item) }}
              </p>
              <p
                v-if="item.metadata?.derivation_error_summary"
                class="mt-2 text-sm text-n-ruby-11"
              >
                {{ item.metadata.derivation_error_summary }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <Button
                  v-if="
                    canManageDocuments &&
                    (item.status === 'FAILED' ||
                      item.metadata?.derivation_status === 'FAILED')
                  "
                  :label="$t('TEAM_LILITH.DOCUMENTS.RETRY_PROCESSING')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="syncing"
                  @click="retryOne(item)"
                />
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.OPEN')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="openDetails(item.id)"
                />
                <Button
                  v-if="
                    canManageDocuments &&
                    item.source_type === 'WEBSITE' &&
                    item.status === 'AVAILABLE'
                  "
                  :label="$t('TEAM_LILITH.DOCUMENTS.SYNC_NOW')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="syncing"
                  @click="syncOne(item)"
                />
                <Button
                  v-if="canManageDocuments"
                  :label="$t('TEAM_LILITH.DOCUMENTS.DELETE')"
                  size="sm"
                  variant="ghost"
                  color="ruby"
                  @click="deleteOne(item)"
                />
              </div>
            </div>
          </div>
        </article>
      </div>

      <div
        v-if="meta.total_pages > 1"
        class="flex items-center justify-center gap-3"
      >
        <Button
          :label="$t('TEAM_LILITH.DOCUMENTS.PREVIOUS')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="meta.page <= 1"
          @click="fetchRecords(meta.page - 1)"
        />
        <span class="text-xs text-n-slate-10">
          {{
            $t('TEAM_LILITH.DOCUMENTS.PAGE_OF', {
              page: meta.page,
              total: meta.total_pages,
            })
          }}
        </span>
        <Button
          :label="$t('TEAM_LILITH.DOCUMENTS.NEXT')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="meta.page >= meta.total_pages"
          @click="fetchRecords(meta.page + 1)"
        />
      </div>

      <section
        v-if="route.query.source_id"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.DOCUMENTS.DETAILS') }}
          </h2>
          <Button
            :label="$t('TEAM_LILITH.DOCUMENTS.CLOSE')"
            size="sm"
            variant="ghost"
            color="slate"
            @click="closeDetails"
          />
        </div>
        <div v-if="detailsLoading" class="py-6 text-sm text-n-slate-10">
          {{ $t('TEAM_LILITH.DOCUMENTS.LOADING_DETAILS') }}
        </div>
        <div v-else-if="detailsError" class="text-sm text-n-ruby-11">
          {{ detailsError }}
        </div>
        <div v-else-if="selectedDocument" class="flex flex-col gap-5">
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ selectedDocument.name }}
              </h3>
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
              >
                {{ statusLabel(selectedDocument.status) }}
              </span>
              <span
                v-if="selectedDocument.source_type === 'WEBSITE'"
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
              >
                {{ syncStatusLabel(selectedDocument.sync_status) }}
              </span>
            </div>
            <a
              v-if="selectedDocument.external_url"
              :href="selectedDocument.external_url"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-1 block break-all text-sm text-n-iris-11 hover:underline"
            >
              {{ selectedDocument.external_url }}
            </a>
            <p v-else class="mt-1 text-sm text-n-slate-10">
              {{ selectedDocument.file_name }}
            </p>
            <p
              v-if="derivationLabel(selectedDocument)"
              class="mt-3 text-sm text-n-slate-11"
            >
              {{ derivationLabel(selectedDocument) }}
            </p>
            <p
              v-if="selectedDocument.metadata?.derivation_error_summary"
              class="mt-3 text-sm text-n-ruby-11"
            >
              {{ selectedDocument.metadata.derivation_error_summary }}
            </p>
            <Button
              v-if="
                canManageDocuments &&
                (selectedDocument.status === 'FAILED' ||
                  selectedDocument.metadata?.derivation_status === 'FAILED')
              "
              :label="$t('TEAM_LILITH.DOCUMENTS.RETRY_PROCESSING')"
              size="sm"
              variant="ghost"
              color="slate"
              :disabled="syncing"
              @click="retryOne(selectedDocument)"
            />
            <p
              v-if="selectedDocument.status === 'FAILED'"
              class="mt-3 rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
            >
              {{
                selectedDocument.error_summary ||
                $t('TEAM_LILITH.DOCUMENTS.FAILED_GENERIC')
              }}
            </p>
            <p
              v-if="selectedDocument.status === 'PROCESSING'"
              class="mt-3 rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-11"
            >
              {{
                selectedDocument.sync_status === 'SYNCING'
                  ? $t('TEAM_LILITH.DOCUMENTS.SYNCING_DESCRIPTION')
                  : $t('TEAM_LILITH.DOCUMENTS.PROCESSING_DESCRIPTION')
              }}
            </p>
            <p
              v-if="
                selectedDocument.source_type === 'WEBSITE' &&
                selectedDocument.sync_status === 'FAILED'
              "
              class="mt-3 rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
            >
              {{
                selectedDocument.sync_error_summary ||
                $t('TEAM_LILITH.DOCUMENTS.SYNC_ERROR_FALLBACK')
              }}
            </p>
            <div
              v-if="canManageDocuments"
              class="mt-3 flex flex-wrap items-center gap-2"
            >
              <Button
                v-if="
                  selectedDocument.source_type === 'WEBSITE' &&
                  selectedDocument.status === 'AVAILABLE'
                "
                :label="$t('TEAM_LILITH.DOCUMENTS.SYNC_NOW')"
                size="sm"
                variant="ghost"
                color="slate"
                :disabled="syncing"
                @click="syncOne(selectedDocument)"
              />
              <span
                v-else-if="selectedDocument.source_type === 'FILE'"
                class="text-xs text-n-slate-9"
              >
                {{ $t('TEAM_LILITH.DOCUMENTS.FILE_NO_SYNC') }}
              </span>
            </div>
          </div>

          <div v-if="selectedDocument.content">
            <div class="mb-2 flex items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('TEAM_LILITH.DOCUMENTS.CONTENT') }}
                </h3>
                <p class="text-xs text-n-slate-9">
                  {{
                    $t('TEAM_LILITH.DOCUMENTS.CHARACTERS', {
                      count: selectedDocument.content.length,
                    })
                  }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="rounded-md px-2 py-1 text-xs"
                  :class="
                    contentView === 'formatted'
                      ? 'bg-n-solid-2 text-n-slate-12'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                  @click="contentView = 'formatted'"
                >
                  {{ $t('TEAM_LILITH.DOCUMENTS.FORMATTED') }}
                </button>
                <button
                  type="button"
                  class="rounded-md px-2 py-1 text-xs"
                  :class="
                    contentView === 'raw'
                      ? 'bg-n-solid-2 text-n-slate-12'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                  @click="contentView = 'raw'"
                >
                  {{ $t('TEAM_LILITH.DOCUMENTS.RAW') }}
                </button>
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.COPY')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  @click="copyContent"
                />
              </div>
            </div>
            <div
              v-if="contentView === 'formatted'"
              class="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-lg border border-n-weak bg-n-surface-1 p-4 text-sm leading-6 text-n-slate-11"
            >
              {{ selectedDocument.content }}
            </div>
            <textarea
              v-else
              rows="16"
              readonly
              :value="selectedDocument.content"
              class="w-full resize-none rounded-lg border border-n-weak bg-n-surface-1 p-4 font-mono text-xs leading-5 text-n-slate-11"
            />
          </div>
          <div
            v-else-if="selectedDocument.status === 'AVAILABLE'"
            class="rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-10"
          >
            {{ $t('TEAM_LILITH.DOCUMENTS.CONTENT_EMPTY') }}
          </div>

          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{
                $t('TEAM_LILITH.DOCUMENTS.RELATED_FAQS', {
                  count: selectedFaqMeta.total_count || 0,
                })
              }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-9">
              {{ $t('TEAM_LILITH.DOCUMENTS.RELATED_FAQS_DESCRIPTION') }}
            </p>
            <div
              v-if="!selectedFaqs.length"
              class="mt-3 text-sm text-n-slate-10"
            >
              {{ $t('TEAM_LILITH.DOCUMENTS.NO_RELATED_FAQS') }}
            </div>
            <div v-else class="mt-3 flex flex-col gap-2">
              <article
                v-for="faq in selectedFaqs"
                :key="faq.id"
                class="rounded-lg border border-n-weak bg-n-surface-1 p-3"
              >
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ faq.title }}
                  </span>
                  <span
                    class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
                  >
                    {{ faq.status }}
                  </span>
                </div>
                <p class="mt-1 text-sm leading-6 text-n-slate-11">
                  {{ faq.content }}
                </p>
              </article>
              <div
                v-if="selectedFaqMeta.total_pages > 1"
                class="flex items-center justify-center gap-2"
              >
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.PREVIOUS')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="selectedFaqMeta.page <= 1"
                  @click="
                    fetchRelatedFaqs(
                      selectedDocument.id,
                      selectedFaqMeta.page - 1
                    )
                  "
                />
                <span class="text-xs text-n-slate-10">
                  {{
                    $t('TEAM_LILITH.DOCUMENTS.PAGE_OF', {
                      page: selectedFaqMeta.page,
                      total: selectedFaqMeta.total_pages,
                    })
                  }}
                </span>
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.NEXT')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="
                    selectedFaqMeta.page >= selectedFaqMeta.total_pages
                  "
                  @click="
                    fetchRelatedFaqs(
                      selectedDocument.id,
                      selectedFaqMeta.page + 1
                    )
                  "
                />
              </div>
            </div>
          </div>

          <div v-if="canViewDocumentUsage">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{
                $t('TEAM_LILITH.DOCUMENTS.USAGE', {
                  count: selectedUsageMeta.total_count || 0,
                })
              }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-9">
              {{ $t('TEAM_LILITH.DOCUMENTS.USAGE_DESCRIPTION') }}
            </p>
            <div
              v-if="!selectedUsage.length"
              class="mt-3 text-sm text-n-slate-10"
            >
              {{ $t('TEAM_LILITH.DOCUMENTS.NO_USAGE') }}
            </div>
            <div v-else class="mt-3 flex flex-col gap-2">
              <article
                v-for="usage in selectedUsage"
                :key="usage.conversation_ref"
                class="rounded-lg border border-n-weak bg-n-surface-1 p-3"
              >
                <a
                  :href="conversationHref(usage.conversation_ref)"
                  class="text-sm font-medium text-n-iris-11 hover:underline"
                >
                  {{
                    $t('TEAM_LILITH.DOCUMENTS.CONVERSATION', {
                      id: usage.conversation_ref,
                    })
                  }}
                </a>
                <p class="mt-1 text-xs text-n-slate-9">
                  {{
                    $t('TEAM_LILITH.DOCUMENTS.LAST_USED', {
                      date: formattedUsageDate(usage.last_used_at),
                    })
                  }}
                </p>
              </article>
              <div
                v-if="selectedUsageMeta.total_pages > 1"
                class="flex items-center justify-center gap-2"
              >
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.PREVIOUS')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="selectedUsageMeta.page <= 1"
                  @click="
                    fetchUsage(selectedDocument.id, selectedUsageMeta.page - 1)
                  "
                />
                <span class="text-xs text-n-slate-10">
                  {{
                    $t('TEAM_LILITH.DOCUMENTS.PAGE_OF', {
                      page: selectedUsageMeta.page,
                      total: selectedUsageMeta.total_pages,
                    })
                  }}
                </span>
                <Button
                  :label="$t('TEAM_LILITH.DOCUMENTS.NEXT')"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :disabled="
                    selectedUsageMeta.page >= selectedUsageMeta.total_pages
                  "
                  @click="
                    fetchUsage(selectedDocument.id, selectedUsageMeta.page + 1)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
